import {useCallback} from 'react';
import {
  GridRowOrderChangeParams,
  GridRowParams,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {Box} from '@mui/material';
import {t} from 'i18next';
import AurDataGridPro from '../../../../common/aur-data-grid-pro/AurDataGridPro';
import {
  AurDataGridTermsOverlay,
  getColumns,
  CustomRowReorderIcon,
  Terms,
} from '../../../../../utils/createSTRUtils';
import AurColorPicker, {
  AurColorPickerOptionsProp,
} from '../../../../common/aur-color-picker/AurColorPicker';
import AurButton from '../../../../common/aur-button/AurButton';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../../hooks/useAnalytics';

interface TermsContentRightProps {
  termsWithColorCodes: Terms[];
  selectedRows: (string | number)[];
  handleRowOrderChange: (params: GridRowOrderChangeParams) => void;
  onRowsSelectionChange: (newSelection: (string | number)[]) => void;
  setSelectedColor: (selectedColor: AurColorPickerOptionsProp) => void;
  deleteTerms: () => void;
}

const TermsContentRight: React.FC<TermsContentRightProps> = ({
  termsWithColorCodes,
  handleRowOrderChange,
  onRowsSelectionChange,
  selectedRows,
  setSelectedColor,
  deleteTerms,
}) => {
  const termListGridRef = useGridApiRef();
  const {trackGAEvent} = useAnalytics();

  const handleTermRowClick = useCallback(
    (param: GridRowParams, event?: MuiEvent<React.MouseEvent>) => {
      if (event) event.defaultMuiPrevented = true;
      const target = event?.target as HTMLElement;

      // Check if click originated from a checkbox or its wrapper
      const isCheckboxClicked =
        target.closest('input[type="checkbox"]') ||
        target.closest('.MuiCheckbox-root');

      if (isCheckboxClicked) {
        return; // Skip row click event has its trigger due to checkbox click
      }
      const {row} = param;
      const {term} = row;
      const selectedRows = termListGridRef.current.getSelectedRows();
      if (selectedRows.has(term)) {
        selectedRows.delete(term);
      } else {
        selectedRows.set(term, row);
      }
      termListGridRef.current.setRowSelectionModel([...selectedRows.keys()]);
    },
    [termListGridRef.current]
  );

  const getTermsWithColorCodesStyle = (termsWithColorCodes: Terms[]) => {
    return termsWithColorCodes.length > 0
      ? `terms-content-grid-non-empty`
      : `terms-content-grid-empty`;
  };

  return (
    <Box
      className={`terms-content-panels-right ${getTermsWithColorCodesStyle(termsWithColorCodes)} `}
    >
      <Box className="terms-content-panels-right-content placeholder-content">
        <div style={{height: '100%', width: '100%'}}>
          <AurDataGridPro
            apiRef={termListGridRef}
            getRowId={(data) => data.term}
            rows={termsWithColorCodes}
            rowReordering
            visibleToolTip
            columns={getColumns()}
            onRowOrderChange={handleRowOrderChange}
            pagination={false}
            hideFooter={true}
            rowHeight={36}
            checkboxSelection={false}
            className="terms-content-aur-datagrid-style"
            headerFilterHeight={48}
            slots={{
              rowReorderIcon: CustomRowReorderIcon,
              noRowsOverlay: AurDataGridTermsOverlay,
              noResultsOverlay: AurDataGridTermsOverlay,
            }}
            onSelectionChange={onRowsSelectionChange}
            handleRowClick={handleTermRowClick}
            selectedIds={selectedRows}
            noRowOverlayClass="placeholder"
            noRowOverlayHeight={100}
          />
        </div>
      </Box>
      <Box className="terms-content-panels-right-footer">
        <div className="terms-content-panels-right-button">
          <AurColorPicker
            buttonType="continue"
            buttonLabel={t('eca.searchTermReport.termsContent.applyButton')}
            buttonSize="Small"
            ariaLabel={t('eca.searchTermReport.termsContent.applyButton')}
            setSelectedColor={(selectedColor: AurColorPickerOptionsProp) => {
              setSelectedColor(selectedColor);
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.str.highlightColor'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.TAB,
                  action: t('gaTracking.str.highlightColorAction'),
                },
                subPage: {
                  subpage_name: t('gaTracking.str.steps.addTerms'),
                  subpage_type: GA_SUBPAGE_TYPE.TAB,
                },
              });
            }}
            disabled={selectedRows.length <= 0}
          />
        </div>
        <div className="terms-content-panels-right-button">
          <AurButton
            type="exit"
            label={t('eca.searchTermReport.termsContent.deleteButton')}
            ariaLabel={t('eca.searchTermReport.termsContent.deleteButton')}
            size="Small"
            onClick={() => {
              deleteTerms();
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.global.delete'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.TAB,
                  action: t('gaTracking.str.deleteTermAction'),
                },
                subPage: {
                  subpage_name: t('gaTracking.str.steps.addTerms'),
                  subpage_type: GA_SUBPAGE_TYPE.TAB,
                },
              });
              trackGAEvent({
                eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                subPage: {
                  subpage_name: t('gaTracking.str.deleteTermsModal'),
                  subpage_type: GA_SUBPAGE_TYPE.MODAL,
                },
              });
            }}
            disabled={selectedRows.length <= 0}
          />
        </div>
      </Box>
    </Box>
  );
};

export default TermsContentRight;
